import { useState } from 'react';
import { Alert, Button, Card, Checkbox, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  getNewsCategories,
  listNews,
  markNewsRead,
  NEWS_PAGE_SIZE,
  type PANewsCategories,
  type PANewsItem,
} from '../api/news';
import { itemsOf, type PAList } from '../api/money';
import { formatInstantDay } from '../format';
import { QueryFrame } from '../money/MoneyPage';
import { Pager } from '../money/Pager';
import { usePAQuery } from '../money/usePAQuery';
import { errorText } from '../session/errorText';

/**
 * ============================================================================
 * «СООБЩЕНИЯ» — the operator's announcements (§28, F28)
 * ============================================================================
 *
 * # The four rules
 *
 *  1. **THE BODY IS RENDERED AS TEXT.** `news_item.body` is free text with no format column,
 *     the authoring screen accepts anything, and a portal interpreting it as HTML would make
 *     every announcement an injection surface on the subscriber's own session — a stored-XSS
 *     vector authored by whoever can write news. This project already ruled that way once,
 *     for the payment-instruction blocks. React escapes it and the CSS keeps the newlines;
 *     `NewsScreen.test.tsx` renders a `<script>` in a body and sweeps the sources for
 *     `dangerouslySetInnerHTML`.
 *  2. **«READ» IS A GESTURE, NEVER A SIDE EFFECT OF ARRIVING.** S22 defines it as «the item
 *     was opened or its pop-up dismissed». So an item is rendered COLLAPSED and OPENING it is
 *     what posts `/read` — nothing is marked by merely landing on the section. Two reasons,
 *     and the second is the load-bearing one: a counter cleared by anything that polls is a
 *     counter that is always zero; and `news_read` rows are what
 *     `GET /mailing/news/{id}/readers` reports BY NAME, so a mark-read he never made would
 *     make the operator's answer to «кому показали объявление?» name people who were never
 *     shown it — worse than a missing row, because it looks like evidence.
 *  3. **THE CATEGORY LIST IS THE SERVER'S, AND THE FILTER OFFERS ONLY WHAT IT NAMES.** An
 *     unknown `category_id` is the flat `404 pa.not_found` — the same refusal a category he
 *     cannot see gets — so a filter built from anything but this read would produce a refusal
 *     he cannot act on.
 *  4. **NO SUBSCRIBE CONTROL HERE.** The checklist is §18's, on «Оповещения», where the
 *     matrix is: the categories decide what ARRIVES, which is a setting rather than a message.
 *     This screen carries a sentence pointing at it — and a MANDATORY item is badged, so «why
 *     am I getting this?» is answered where the question is asked rather than one section
 *     away.
 *
 * ⚠ WHAT §28 DESCRIBES AND THIS BUILD DOES NOT HAVE: the main-page BANNER and the POP-UP.
 * Both are operator-configurable in the spec and NEITHER HAS A SETTINGS KEY anywhere in this
 * system (verified 2026-08-12, and recorded in §28 as NOT BUILT rather than as toggled off).
 * The wire carries everything a banner would need — `unread` per category and `unread_total`
 * — so the day the key exists the data is already there.
 */
export function NewsScreen() {
  const { t } = useTranslation();
  const [categoryID, setCategoryID] = useState<number | null>(null);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [offset, setOffset] = useState(0);

  const categories = usePAQuery<PANewsCategories>(() => getNewsCategories(), []);
  const { query, reload } = usePAQuery<PAList<PANewsItem>>(
    () => listNews({ categoryID, unread: unreadOnly, offset }),
    [categoryID, unreadOnly, offset],
  );

  return (
    <>
      <h1 className="pa-page-title">{t('news.title')}</h1>
      <p className="pa-page-lead">{t('news.lead')}</p>

      {/* The filter renders only once the categories are known — see rule 3. Its failure is
          not this screen's: the LIST still reads, unfiltered, which is the whole section. */}
      {categories.query.status === 'ready' ? (
        <div className="pa-filters">
          <div className="pa-spinner-field">
            <label className="pa-field-label" htmlFor="pa-news-category">
              {t('news.categoryLabel')}
            </label>
            <Select<number>
              id="pa-news-category"
              className="pa-picker-select"
              size="large"
              value={categoryID ?? 0}
              onChange={(next) => {
                setOffset(0);
                setCategoryID(next === 0 ? null : next);
              }}
              options={[
                { value: 0, label: t('news.allCategories') },
                ...categories.query.data.items.map((c) => ({
                  value: c.id,
                  label:
                    c.unread > 0
                      ? t('news.categoryOptionUnread', { name: c.name, count: c.unread })
                      : c.name,
                })),
              ]}
            />
          </div>
          <Checkbox
            checked={unreadOnly}
            onChange={(e) => {
              setOffset(0);
              setUnreadOnly(e.target.checked);
            }}
          >
            {t('news.unreadOnly')}
          </Checkbox>
        </div>
      ) : null}

      {/* The section's own total, from the SERVER's sum — never added up from a page this
          screen may have truncated. */}
      {categories.query.status === 'ready' && categories.query.data.unread_total > 0 ? (
        <Alert
          type="info"
          showIcon
          message={t('news.unreadTotal', { count: categories.query.data.unread_total })}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      <QueryFrame query={query} reload={reload}>
        {(list) => (
          <NewsList
            rows={itemsOf(list)}
            total={list.total}
            offset={offset}
            unreadOnly={unreadOnly}
            onOffset={setOffset}
            onRead={categories.reload}
          />
        )}
      </QueryFrame>

      {/* RULE 4: a sentence, not a control. */}
      <p className="pa-field-hint">{t('news.categoriesElsewhere')}</p>
    </>
  );
}

function NewsList({
  rows,
  total,
  offset,
  unreadOnly,
  onOffset,
  onRead,
}: {
  rows: readonly PANewsItem[];
  total: number;
  offset: number;
  unreadOnly: boolean;
  onOffset: (next: number) => void;
  /** Re-reads the CATEGORY counts, which the read just changed. */
  onRead: () => void;
}) {
  const { t } = useTranslation();

  if (rows.length === 0) {
    return (
      <Alert
        type="info"
        showIcon
        message={t(unreadOnly ? 'news.emptyUnread' : 'news.empty')}
      />
    );
  }

  return (
    <>
      {rows.map((item) => (
        <NewsItem key={item.id} item={item} onRead={onRead} />
      ))}
      <Pager
        offset={offset}
        pageSize={NEWS_PAGE_SIZE}
        shown={rows.length}
        total={total}
        onOffset={onOffset}
      />
    </>
  );
}

/** One announcement. Collapsed until he opens it, and opening it is the READ — see rule 2. */
function NewsItem({ item, onRead }: { item: PANewsItem; onRead: () => void }) {
  const { t } = useTranslation();
  const [row, setRow] = useState<PANewsItem>(item);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (!next || row.read) return;
    setError(null);
    // The read is recorded and the FRESH item is what replaces ours — the server keeps the
    // FIRST timestamp on a double submit, which is the one the operator's readers report shows.
    void markNewsRead(row.id)
      .then((fresh) => {
        setRow(fresh);
        onRead();
      })
      .catch(setError);
  };

  return (
    <Card className="pa-block pa-news-item" data-testid={`pa-news-${row.id}`}>
      <button className="pa-news-head" type="button" aria-expanded={open} onClick={toggle}>
        <span className="pa-news-date">{formatInstantDay(row.published_at)}</span>
        <span className="pa-news-subject">{row.subject}</span>
        <span className="pa-news-marks">
          <span className="pa-status-badge">{row.category_name}</span>
          {row.mandatory ? (
            <span className="pa-status-badge">{t('news.mandatory')}</span>
          ) : null}
          {!row.read ? <span className="pa-status pa-status-warn">{t('news.new')}</span> : null}
        </span>
      </button>
      {error ? (
        <Alert type="error" showIcon message={errorText(error, t)} style={{ marginTop: 12 }} />
      ) : null}
      {open ? (
        <>
          {/* RULE 1 — the operator's own text, escaped by React, newlines kept by the CSS. */}
          <p className="pa-news-body">{row.body}</p>
          <div className="pa-actions">
            <Button size="large" onClick={() => setOpen(false)}>
              {t('news.collapse')}
            </Button>
          </div>
        </>
      ) : null}
    </Card>
  );
}
